"use client";

import {
  CallScoringService,
  type CallScoringCallDetailSuccessResponse,
} from "@/api-client/call-scoring";
import { useAuthenticatedQuery } from "./use-authenticated-query";
import type { CallScoringDetail, ScoringValueTone } from "@/lib/call-scoring-types";
import { getScoreTone } from "@/lib/score-tone";

function toTitle(text: string): string {
  return text
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function toToneFromLabel(label?: string | null): ScoringValueTone {
  const value = (label || "").toLowerCase();
  if (value.includes("strong")) return "success";
  if (value.includes("mixed") || value.includes("moderate")) return "warning";
  if (value.includes("low") || value.includes("start")) return "danger";
  return "neutral";
}

function badgeClassFromTone(tone: ScoringValueTone): string {
  if (tone === "success") return "bg-success/10 text-success hover:bg-success/15";
  if (tone === "warning") return "bg-warning/10 text-warning hover:bg-warning/15";
  if (tone === "danger") return "bg-danger/10 text-danger hover:bg-danger/15";
  return "bg-muted/30 text-muted-foreground hover:bg-muted/40";
}

function formatNum(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function prepScoreLabel(value: number): string {
  return `${formatNum(value)}/5`;
}

function deliveryScoreLabel(value: number): string {
  return `${formatNum(value)}/5`;
}

function mapToDrawerData(api: CallScoringCallDetailSuccessResponse): CallScoringDetail {
  const prepScore = toNumber(api?.header?.prep_score);
  const deliveryScore = toNumber(api?.header?.delivery_score);
  const prepTone = toToneFromLabel(api?.header?.prep_label);
  const deliveryTone = toToneFromLabel(api?.header?.delivery_label);

  return {
    preparationScore: `${formatNum(prepScore)}/5`,
    preparationBadge: {
      label: api?.header?.prep_label || "Preparation",
      className: badgeClassFromTone(prepTone),
    },
    deliveryScore: `${formatNum(deliveryScore)}/5`,
    deliveryBadge: {
      label: api?.header?.delivery_label || "Delivery",
      className: badgeClassFromTone(deliveryTone),
    },
    executiveSummaryTitle: api?.outcome?.label || "Call Outcome",
    executiveSummaryBody: api?.outcome?.description || "No summary available.",
    preparationMetrics: (api?.preparation?.dimensions || []).map((d) => {
      const weight = toNumber(d?.weight);
      const score = toNumber(d?.score);
      const contribution = toNumber(d?.contribution);
      const hasEntries = Boolean(d.entries && d.entries.length > 0);
      return {
        id: d?.name || "unknown",
        title: toTitle(d?.name || "unknown"),
        weightLabel: `${formatNum(weight)}%`,
        scoreLabel: prepScoreLabel(score),
        scoreTone: getScoreTone(score),
        contributionLabel: `${formatNum(contribution)}`,
        contributionValue: contribution,
        collapsible: hasEntries,
        defaultOpen: hasEntries,
        lineItems: hasEntries
          ? d.entries!.map((entry) => ({
              label: entry?.entry || "--",
              value: formatNum(toNumber(entry?.score)),
              tone: (() => {
                const tone = getScoreTone(toNumber(entry?.score), {
                  warningMin: 0.5,
                  successMin: 1,
                });
                return tone === "neutral" ? "danger" : tone;
              })(),
            }))
          : undefined,
        narrative:
          !hasEntries && (d.note || d.improvement)
            ? `${d.note || ""}${d.note && d.improvement ? " - " : ""}${d.improvement || ""}`
            : undefined,
      };
    }),
    deliveryMetrics: (api?.delivery?.dimensions || []).map((d) => {
      const weight = toNumber(d?.weight);
      const score = toNumber(d?.score);
      const contribution = toNumber(d?.contribution);
      return {
      id: d?.name || "unknown",
      title: toTitle(d?.name || "unknown"),
      description: d?.evidence || d?.negative_signal?.reason || "No details provided.",
      weightLabel: `${formatNum(weight)}%`,
      scoreLabel: deliveryScoreLabel(score),
      scoreTone: getScoreTone(score),
      contributionLabel: formatNum(contribution),
      contributionValue: contribution,
      };
    }),
    finalPrepScore: `${formatNum(toNumber(api?.preparation?.final_score))}/5`,
    finalDeliveryScore: `${formatNum(toNumber(api?.delivery?.final_score))}/5`,
    improvements: (api?.coaching || []).map((c) => ({
      lead: toTitle(c?.area || "general"),
      emphasis: c?.action || "Improve",
      description: c?.tip || "No coaching tip provided.",
    })),
  };
}

export function useCallScoringCallDetail(meetingId: number | null) {
  const query = useAuthenticatedQuery({
    queryKey: ["call-scoring-call-detail", meetingId ?? 0],
    enabled: Boolean(meetingId),
    retry: false,
    staleTime: 60 * 1000,
    queryFn: () => {
      if (!meetingId) throw new Error("Meeting ID is required");
      return CallScoringService.getCallDetail(meetingId);
    },
  });

  const detailStatus = query.data?.status;
  const isTranscriptInsufficient = detailStatus === "TRANSCRIPT_INSUFFICIENT";
  const hasScoringPayload = Boolean(
    query.data &&
      "header" in query.data &&
      "preparation" in query.data &&
      "delivery" in query.data &&
      "coaching" in query.data
  );

  let drawerData: CallScoringDetail | null = null;
  try {
    drawerData =
      query.data && hasScoringPayload
        ? mapToDrawerData(query.data as CallScoringCallDetailSuccessResponse)
        : null;
  } catch (error) {
    console.error("Failed to map call scoring detail response:", error);
    drawerData = null;
  }

  return {
    ...query,
    drawerData,
    detailStatus,
    isTranscriptInsufficient,
  };
}

